/**
 * Tic-Tac-Logic Grid Operations
 *
 * Provides functions for working with the puzzle grid.
 */

import { type CellValue, type GridSize, EMPTY, X, O } from './types';

/**
 * Get cell index from coordinates
 */
export function getIndex(size: number, x: number, y: number): number {
  return y * size + x;
}

/**
 * Get coordinates from index
 */
export function getCoords(size: number, index: number): { x: number; y: number } {
  return {
    x: index % size,
    y: Math.floor(index / size),
  };
}

/**
 * Get cell value at coordinates
 */
export function getCell(cells: CellValue[], size: number, x: number, y: number): CellValue {
  if (x < 0 || x >= size || y < 0 || y >= size) return EMPTY;
  return cells[getIndex(size, x, y)] ?? EMPTY;
}

/**
 * Set cell value at coordinates
 */
export function setCell(cells: CellValue[], size: number, x: number, y: number, value: CellValue): void {
  if (x < 0 || x >= size || y < 0 || y >= size) return;
  cells[getIndex(size, x, y)] = value;
}

/**
 * Get row values
 */
export function getRow(cells: CellValue[], size: number, row: number): CellValue[] {
  const result: CellValue[] = [];
  for (let x = 0; x < size; x++) {
    result.push(getCell(cells, size, x, row));
  }
  return result;
}

/**
 * Get column values
 */
export function getColumn(cells: CellValue[], size: number, col: number): CellValue[] {
  const result: CellValue[] = [];
  for (let y = 0; y < size; y++) {
    result.push(getCell(cells, size, col, y));
  }
  return result;
}

/**
 * Count symbols in a line
 */
export function countSymbols(line: CellValue[]): { x: number; o: number; empty: number } {
  let x = 0;
  let o = 0;
  let empty = 0;

  for (const cell of line) {
    if (cell === X) x++;
    else if (cell === O) o++;
    else empty++;
  }

  return { x, o, empty };
}

/**
 * Count symbols in a row
 */
export function countRowSymbols(
  cells: CellValue[],
  size: number,
  row: number
): { x: number; o: number; empty: number } {
  return countSymbols(getRow(cells, size, row));
}

/**
 * Count symbols in a column
 */
export function countColumnSymbols(
  cells: CellValue[],
  size: number,
  col: number
): { x: number; o: number; empty: number } {
  return countSymbols(getColumn(cells, size, col));
}

/**
 * Check if a line has three or more consecutive same symbols
 */
export function hasThreeConsecutive(line: CellValue[]): boolean {
  let consecutive = 1;
  let lastValue: CellValue = EMPTY;

  for (const cell of line) {
    if (cell !== EMPTY && cell === lastValue) {
      consecutive++;
      if (consecutive >= 3) return true;
    } else {
      consecutive = 1;
    }
    lastValue = cell;
  }

  return false;
}

/**
 * Find positions of three consecutive same symbols
 */
export function findThreeConsecutive(line: CellValue[]): number[] {
  const positions: number[] = [];
  let startIndex = 0;
  let consecutive = 1;
  let lastValue: CellValue = EMPTY;

  for (let i = 0; i < line.length; i++) {
    const cell = line[i]!;
    if (cell !== EMPTY && cell === lastValue) {
      consecutive++;
      if (consecutive >= 3) {
        // Add all positions in this run
        for (let j = startIndex; j <= i; j++) {
          if (!positions.includes(j)) positions.push(j);
        }
      }
    } else {
      consecutive = 1;
      startIndex = i;
    }
    lastValue = cell;
  }

  return positions;
}

/**
 * Check if two lines are identical (and both complete)
 */
export function linesIdentical(line1: CellValue[], line2: CellValue[]): boolean {
  if (line1.length !== line2.length) return false;

  // Both must be complete (no empty cells)
  for (let i = 0; i < line1.length; i++) {
    if (line1[i] === EMPTY || line2[i] === EMPTY) return false;
    if (line1[i] !== line2[i]) return false;
  }

  return true;
}

/**
 * Check if a line is complete (no empty cells)
 */
export function isLineComplete(line: CellValue[]): boolean {
  return line.every((cell) => cell !== EMPTY);
}

/**
 * Clone cells array
 */
export function cloneCells(cells: CellValue[]): CellValue[] {
  return [...cells];
}

/**
 * Create empty cells array
 */
export function createEmptyCells(size: GridSize): CellValue[] {
  return new Array(size * size).fill(EMPTY);
}

/**
 * Find all empty cells
 */
export function findEmptyCells(cells: CellValue[], size: number): Array<{ x: number; y: number }> {
  const result: Array<{ x: number; y: number }> = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (getCell(cells, size, x, y) === EMPTY) {
        result.push({ x, y });
      }
    }
  }
  return result;
}

/**
 * Count filled cells
 */
export function countFilledCells(cells: CellValue[]): number {
  return cells.filter((c) => c !== EMPTY).length;
}

/**
 * Convert cells to 2D array for display
 */
export function cellsTo2D(cells: CellValue[], size: number): CellValue[][] {
  const result: CellValue[][] = [];
  for (let y = 0; y < size; y++) {
    result.push(getRow(cells, size, y));
  }
  return result;
}

/**
 * Convert 2D array to flat cells
 */
export function cellsFrom2D(grid: CellValue[][]): CellValue[] {
  return grid.flat();
}

/**
 * Get string representation for debugging
 */
export function cellsToString(cells: CellValue[], size: number): string {
  const symbols = ['.', 'X', 'O'];
  let result = '';
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      result += symbols[getCell(cells, size, x, y)] + ' ';
    }
    result += '\n';
  }
  return result;
}
